import React, { useRef, useState, useEffect } from 'react';
import moment from 'moment';

import SVGIcon, { BASE_ICONS } from '../svg';
import { remCss, rgba, scssVariables } from '../../helpers';
import { MCButton, MCText, View } from '../basic';
import LANG_CONST from '../../helpers/lang/const';

const DayItem = ({ dataDay, title, label = '', active, onClick, enabled = true }) => {
  return (
    <MCButton
      light={!active}
      disabled={!enabled}
      hover={enabled}
      dark={active}
      rounded
      small
      data-day={dataDay}
      onClick={onClick}
      style={{
        padding: remCss(13),
        minWidth: remCss(55),
        ...(!enabled
          ? { cursor: 'default', backgroundColor: rgba(scssVariables['colors-mc-heading'], 0.05) }
          : {})
      }}
    >
      <View style={{ gap: remCss(13), alignItems: 'center' }}>
        <MCText label={title} size={20} lineHeight={20} weight={500} white={active} />
        <MCText label={label} size={14} lineHeight={14} white={active} />
        <View
          style={{
            width: remCss(8),
            height: remCss(8),
            borderRadius: remCss(4),
            backgroundColor: active ? 'white' : 'transparent'
          }}
        />
      </View>
    </MCButton>
  );
};

export const CarrouselDaysOfMonth = ({
  style = {},
  selectedDay,
  handleChangeSelectedDay,
  all = false,
  startDate,
  endDate,
  enabledFn
}) => {
  const containerRef = useRef(null);
  const [canScroll, setCanScroll] = useState(false); // Track if content overflows

  // Check if content overflows whenever the list or layout changes
  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const { scrollWidth, clientWidth } = containerRef.current;
        // True if the actual content is wider than the visible wrapper
        setCanScroll(scrollWidth > clientWidth);
      }
    };

    checkOverflow();

    // Re-verify on window resize to ensure responsiveness
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [startDate]); // Re-run if the total number of items changes

  useEffect(() => {
    if (!containerRef.current) return;

    // Resolve selector parameter string matching our key patterns
    const targetValue = selectedDay === null ? 'all' : moment(selectedDay).format('YYYYMMDD');
    const activeChildElement = containerRef.current.querySelector(`[data-day="${targetValue}"]`);

    let frameTimer = null;
    if (activeChildElement) {
      // Small timeout frame ensures layout paint renders before running calculations
      frameTimer = setTimeout(() => {
        activeChildElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
      }, 50);
    }
    return () => frameTimer && clearTimeout(frameTimer);
  }, [selectedDay]);

  const handleScroll = (direction) => {
    if (!containerRef.current) return;

    const distance = 240;
    const currentScroll = containerRef.current.scrollLeft;

    containerRef.current.scrollTo({
      behavior: 'smooth',
      left: direction === 'left' ? currentScroll - distance : currentScroll + distance
    });
  };

  // Return early if bounding dates are missing to prevent rendering bugs
  if (!startDate || !endDate) return null;

  let list = all
    ? [
        <DayItem
          active={selectedDay === null}
          className={'mc-day ' + (selectedDay === null ? 'active' : '')}
          key={'mc-day-all'}
          dataDay={'all'}
          title={LANG_CONST.all}
          onClick={() => {
            if (selectedDay === null) return;
            handleChangeSelectedDay(null);
          }}
        />
      ]
    : [];

  let dateCursor = moment(startDate).clone();
  const finalDate = moment(endDate);

  while (!dateCursor.isAfter(finalDate, 'day')) {
    const timestamp = dateCursor.valueOf();
    const isCurrentSelected = selectedDay && dateCursor.isSame(selectedDay, 'day');
    const enabled = enabledFn ? enabledFn(dateCursor) : true;

    list.push(
      <DayItem
        active={isCurrentSelected}
        enabled={enabled}
        dataDay={dateCursor.format('YYYYMMDD')}
        key={'mc-day-' + timestamp}
        onClick={() => {
          if (isCurrentSelected) return;
          handleChangeSelectedDay(timestamp);
        }}
        title={dateCursor.date()}
        label={dateCursor.format('ddd').toUpperCase()}
      />
    );

    dateCursor.add(1, 'day');
  }

  if (list.length === 1 || (all && list.length === 2)) return null;

  return (
    <View style={{ flexDirection: 'row', gap: remCss(15), ...style }}>
      {canScroll && (
        <View style={{ height: '100%', minWidth: remCss(40) }}>
          <MCButton
            light
            flex
            style={{ borderRadius: remCss(7) }}
            tiny
            hover
            onClick={() => handleScroll('left')}
          >
            <SVGIcon
              iconname={BASE_ICONS.LEFT}
              fill={scssVariables['colors-mc-primary']}
              width={remCss(40)}
            />
          </MCButton>
        </View>
      )}
      <div style={{ display: 'grid' }}>
        <div
          ref={containerRef}
          className='mc-days-container'
          style={{
            display: 'flex',
            overflowX: 'hidden',
            gap: remCss(15),
            flex: 1,
            width: '100%', // Added to lock size to local layout parent
            minWidth: 0, // Added to prevent flex item blowouts
            whiteSpace: 'nowrap'
          }}
        >
          {list}
        </div>
      </div>
      {canScroll && (
        <View style={{ height: '100%', minWidth: remCss(40) }}>
          <MCButton
            light
            flex
            style={{ borderRadius: remCss(7) }}
            tiny
            hover
            onClick={() => handleScroll('right')}
          >
            <SVGIcon
              iconname={BASE_ICONS.RIGHT}
              fill={scssVariables['colors-mc-primary']}
              width={remCss(40)}
            />
          </MCButton>
        </View>
      )}
    </View>
  );
};
